/**
 * EducationGuideBody — the rendered Patient Education Guide, with no page
 * chrome (Card P77 item 10, 10 Oct 2026).
 *
 * ⚠⚠ EXTRACTED RATHER THAN COPIED, DELIBERATELY.
 *
 * Item 10 needs the guide on a second surface: inside the CRM, for staff,
 * where the public page at /education cannot go (it short-circuits the auth
 * shell in App.js, so navigating there from the CRM destroys the session UI
 * the operator was working in).
 *
 * The obvious way to get it there is to copy the section-rendering JSX out of
 * LeadEducationGuidePage. This repository has paid for that move more than
 * once — a parser bug fixed in a helper survived in a second inline copy, and
 * a fixed-width scan control was hollow in exactly one of its two homes. A
 * guide rendered twice drifts: someone adds a section wrapper, or fixes the
 * disclaimer styling, in the copy they happen to have open.
 *
 * So the public page and the CRM modal both render THIS. The content itself
 * stays where it was, in src/lib/educationGuideContent.js.
 *
 * ⛔ NO PHI, AND NOT BY POLICY — BY SHAPE. This component takes `lang` and
 * nothing else. There is no contact prop, no patient prop, no interpolation:
 * EDUCATION_GUIDE is a frozen literal of generic clinical education copy with
 * no placeholders in it. That is what makes the print-to-PDF path below safe
 * to hand to a patient, and it is why a generic filename is honest rather
 * than a fig leaf. If a future edit adds a {{token}} to the content module,
 * this comment stops being true — check it before you do.
 */
import React from 'react';
import { EDUCATION_GUIDE } from '../lib/educationGuideContent';

export default function EducationGuideBody({ lang = 'en' }) {
  const guide = EDUCATION_GUIDE[lang] || EDUCATION_GUIDE.en;

  return (
    <div className="education-guide-body">
      <h1 style={{
        margin: '0 0 18px', fontSize: '1.35rem', color: '#111827', lineHeight: 1.25,
      }}>
        {guide.title}
      </h1>

      {guide.sections.map((s, i) => (
        <section key={i} style={{ marginBottom: 18 }}>
          <h2 style={{
            fontSize: s.disclaimer ? '0.95rem' : '1.05rem',
            color: s.disclaimer ? '#6B7280' : '#1F2937',
            margin: '0 0 6px', borderBottom: '1px solid #F1F5F9', paddingBottom: 4,
          }}>
            {s.h}
          </h2>
          {s.p && (
            <p style={{
              margin: 0, fontSize: s.disclaimer ? '0.8rem' : '0.92rem',
              color: s.disclaimer ? '#6B7280' : '#374151', lineHeight: 1.6,
              fontStyle: s.disclaimer ? 'italic' : 'normal',
            }}>
              {s.p}
            </p>
          )}
          {s.list && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 22 }}>
              {s.list.map((item, j) => (
                <li key={j} style={{ fontSize: '0.9rem', color: '#374151', lineHeight: 1.6, marginBottom: 2 }}>
                  {item}
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}

      <p style={{ marginTop: 24, textAlign: 'center', fontSize: '0.72rem', color: '#9CA3AF' }}>
        Zignagenix Regenerative Medicine &middot; Tijuana &middot; Canc&uacute;n &middot; Puerto Vallarta
      </p>
    </div>
  );
}
